import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DepositCss from '../css/DepositCss';
import { useNavigate } from 'react-router-dom';
import { Context } from '../Context/Context';
import { Flip, toast } from 'react-toastify';
import { AddClientApi } from '../Api/AddClient';
import { GetAlClientsApi } from '../Api/GetAllClients';
import { Helmet } from 'react-helmet';


export default function AddClient() {
  const [clientName, setClientName] = useState('');
  const [commission, setCommission] = useState('');
  const [passingRate, setPassingRate] = useState('');
  const [patti, setPatti] = useState('');
  const [clientList, setClientList] = useState([]);

  const navigate = useNavigate();
  const { setLoad, setLoadColor, setSessionDetail } = useContext(Context);

  // ✅ Memoize userInfo to prevent re-creation every render
  const userInfo = useMemo(() => {
    return JSON.parse(localStorage.getItem('userinfo'));
  }, []);

  // ✅ Stable fetchClientList
  const fetchClientList = useCallback(async () => {
    try {
      const res = await GetAlClientsApi({
        username: userInfo?.username,
        user_id: userInfo?.userId
      });

      if (res.data.status) {
        setClientList(res.data.data);
      } else {
        setClientList([]);
        console.log(res.data.message);
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  }, [userInfo]);

  useEffect(() => {
    fetchClientList();
  }, [fetchClientList]);

  const handleEditClick = (client) => {
    navigate("/EditClient");
  };

  const handleAddClient = async () => {
    if (!clientName.trim() || !passingRate.trim()) {
      toast.error("Please fill all required fields", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
        transition: Flip
      });
      return;
    }

    setLoad(true);
    setLoadColor("#434343");

    const clientData = {
      username: userInfo?.username,
      user_id: userInfo?.userId,
      client_name: clientName,
      lc: commission ? Number(commission) : 0,
      passing_rate: passingRate,
      patti: patti
    };

    try {
      const res = await AddClientApi(clientData);
      if (res.data.status) {
        setSessionDetail(prev => ({
          ...prev,
          user_clients: res.data.data.user_clients
        }));
        toast.success('Client added successfully!', {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
          transition: Flip
        });
        fetchClientList(); // Refresh list after add
        // Optionally: navigate("/game");
      } else {
        toast.error(res.data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Flip
        });
      }
    } catch (error) {
      toast.error("Something went wrong", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
        transition: Flip
      });
    } finally {
      setLoad(false);
    }
  };

  return (
    <DepositCss>
      <Helmet>
        {/* Page Title */}
        <title>Add Client | Manage Your Matka Clients | MatkaJantri</title>

        {/* SEO Description */}
        <meta
          name="description"
          content="Add new clients to your Matka satta hisab kitab easily. Manage your client list for accurate record keeping on MatkaJantri."
        />

        {/* SEO Directives */}
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://pushpabetfair.com/add-client" />

        {/* Favicon and Icons */}
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />

        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph */}
        <meta property="og:title" content="Add Client | Manage Your Matka Clients | Matka Jantri" />
        <meta
          property="og:description"
          content="Add new clients to your Matka satta hisab kitab easily. Manage your client list for accurate record keeping on MatkaJantri."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pushpabetfair.com/add-client" />
        <meta property="og:image" content="https://app.matkajantri.com/JantriImage/Logo.png" />
      </Helmet>

      <div className="w-100 fullpage">
        <div className="w-3">
          <div className="back-top">
            <i className="fa-solid fa-chevron-left" onClick={() => navigate("/game")}></i>
          </div>

          <div className="w-100 text-center d-flex">
            <button className="addFunds">
              <i className="fa-solid fa-user-plus"></i> Add Client
            </button>
          </div>

          <div className="w-100 px-3">
            {/* Form */}
            <div className="mb-3 w-100">
              <label className="form-label">Client Name <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                className="form-control ps-2"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </div>

            <div className="mb-3 w-100">
              <label className="form-label">L.C. (Commission%)</label>
              <input
                type="number"
                className="form-control ps-2"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
              />
            </div>

            <div className="mb-3 w-100">
              <label className="form-label">Passing Rate <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                className="form-control ps-2"
                value={passingRate}
                onChange={(e) => setPassingRate(e.target.value)}
              />
            </div>

            <div className="mb-3 w-100">
              <label className="form-label">Patti</label>
              <input
                type="text"
                className="form-control ps-2"
                value={patti}
                onChange={(e) => setPatti(e.target.value)}
              />
            </div>

            <div className="text-center">
              <button className="submit-btn support-btn" onClick={handleAddClient}>
                Submit
              </button>
            </div>
          </div>

          {/* Client List */}
          <div className="w-100 px-3 mt-4">
            <h4 className="mb-3">Client List</h4>
            {clientList.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Client Name</th>
                      <th>LC</th>
                      <th>Passing Rate</th>
                      <th>Patti</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientList.map((client) => (
                      <tr key={client._id}>
                        <td>{client.client_name}</td>
                        <td>{client.lossCommission}</td>
                        <td>{client.passing_rate}</td>
                        <td>{client.patti}</td>
                        <td>{new Date(client.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEditClick(client)}
                          >
                            <i className="fa fa-edit"></i> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No clients found.</p>
            )}
          </div>
        </div>
      </div>
    </DepositCss>
  );
}
